import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Form, 
  Input, 
  Button, 
  Select, 
  message, 
  Spin,
  Typography,
  Space,
  Row,
  Col,
  App
} from 'antd';
import { 
  UserOutlined, 
  MailOutlined, 
  SaveOutlined,
  ArrowLeftOutlined
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import userService from '../services/userService';
import groupService from '../services/groupService';
import businessUnitService from '../services/businessUnitService';
import PageHeader from './PageHeader';
import BreadcrumbNav, { BREADCRUMB_PATTERNS } from './BreadcrumbNav';
import PhoneNumberInput from './PhoneNumberInput';

const { Title, Text } = Typography;
const { Option } = Select;

const EditTeamMember = () => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [groups, setGroups] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedBusinessUnit, setSelectedBusinessUnit] = useState(null);
  const [selectedUserType, setSelectedUserType] = useState('regular');
  const [phoneError, setPhoneError] = useState(null);
  const [selectedCountryCode, setSelectedCountryCode] = useState('+27');
  const [hasFormErrors, setHasFormErrors] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();

  // Check for form errors whenever form changes
  useEffect(() => {
    const checkFormErrors = () => {
      const fieldErrors = form.getFieldsError();
      const hasErrors = fieldErrors.some(({ errors }) => errors.length > 0) || !!phoneError;
      setHasFormErrors(hasErrors);
    };

    // Check immediately
    checkFormErrors();

    // Set up form field listener
    const handleFieldsChange = () => {
      setTimeout(checkFormErrors, 100); // Small delay to ensure form state is updated
    };

    // Listen to form changes
    form.getInternalHooks('RC_FORM_INTERNAL_HOOKS').registerWatch(handleFieldsChange);

    return () => {
      // Cleanup if needed
    };
  }, [form, phoneError]);

  useEffect(() => {
    const loadData = async () => {
      await loadGroups();
      await loadBusinessUnits();
      await loadUser();
    };
    loadData();
  }, [id]);

  // Update business unit dropdown when business units are loaded
  useEffect(() => {
    if (user && businessUnits.length > 0 && user.businessUnit) {
      const businessUnitId = user.businessUnit._id || user.businessUnit;
      setSelectedBusinessUnit(businessUnitId);
      
      // Update departments if business unit has them
      const businessUnitData = businessUnits.find(bu => bu._id === businessUnitId);
      if (businessUnitData && businessUnitData.departments) {
        setDepartments(businessUnitData.departments);
      }
      
      // Re-set the form value to ensure proper display
      form.setFieldsValue({
        businessUnit: businessUnitId
      });
    }
  }, [businessUnits, user]);

  const loadGroups = async () => {
    try {
      const response = await groupService.getAllGroups();
      if (response.success && Array.isArray(response.data)) {
        setGroups(response.data);
      } else {
        setGroups([]);
      }
    } catch (error) {
      console.error('Error loading groups:', error);
      setGroups([]);
    }
  };

  const loadBusinessUnits = async () => {
    try {
      console.log('EditTeamMember - Loading business units...');
      const response = await businessUnitService.getAllBusinessUnits();
      console.log('EditTeamMember - Business units response:', response);
      
      if (response.success && Array.isArray(response.data)) {
        setBusinessUnits(response.data);
        console.log('EditTeamMember - Business units set successfully:', response.data.length, 'items');
      } else {
        console.error('Business units response is not an array:', response);
        setBusinessUnits([]); // Ensure businessUnits is always an array
      }
    } catch (error) {
      console.error('Error loading business units:', error);
      setBusinessUnits([]); // Ensure businessUnits is always an array on error
    }
  };

  const handleBusinessUnitChange = (businessUnitId) => {
    setSelectedBusinessUnit(businessUnitId);
    
    // Find the selected business unit and update departments
    const selectedBU = businessUnits.find(bu => bu._id === businessUnitId);
    if (selectedBU && selectedBU.departments) {
      setDepartments(selectedBU.departments);
    } else {
      setDepartments([]);
    }
    
    // Clear department selection when business unit changes
    form.setFieldsValue({ department: undefined });
  };

  const handleUserTypeChange = (userType) => {
    setSelectedUserType(userType);
    
    // Clear group selection if user type is participant (participants don't have groups)
    if (userType === 'participant') {
      form.setFieldsValue({ role: undefined });
    }
    
    // Clear business unit/department for regular users (they don't need it)
    if (userType === 'regular') {
      form.setFieldsValue({ 
        businessUnit: undefined, 
        department: undefined 
      });
      setSelectedBusinessUnit(null);
      setDepartments([]);
    }
  };

  const loadUser = async () => {
    try {
      setPageLoading(true);
      const result = await userService.getUserById(id);
      console.log('EditTeamMember - loadUser result:', result);
      
      if (result.success && result.data) {
        const userData = result.data;
        setUser(userData);
        console.log('EditTeamMember - userData:', userData);
        
        // Parse phone number
        let phoneNumber = '';
        let countryCode = '+27';
        
        if (userData.phoneNumber) {
          if (typeof userData.phoneNumber === 'object') {
            countryCode = userData.phoneNumber.countryCode || '+27';
            phoneNumber = userData.phoneNumber.number || '';
          } else if (typeof userData.phoneNumber === 'string' && userData.phoneNumber.startsWith('+')) {
            // Parse full phone number
            const countryCodes = [
              { dial: '+27' }, { dial: '+1' }, { dial: '+44' }, { dial: '+33' },
              { dial: '+49' }, { dial: '+91' }, { dial: '+86' }, { dial: '+81' }
            ];
            for (const country of countryCodes) {
              if (userData.phoneNumber.startsWith(country.dial)) {
                countryCode = country.dial;
                phoneNumber = userData.phoneNumber.substring(country.dial.length);
                break;
              }
            }
          } else {
            countryCode = '+27';
            phoneNumber = userData.phoneNumber;
          }
        }
        
        setSelectedCountryCode(countryCode);
        
        // Set selected user type
        const userType = userData.userType || 'regular';
        setSelectedUserType(userType);
        
        // Set selected business unit and load departments if user has business unit
        if (userData.businessUnit) {
          const businessUnitId = userData.businessUnit._id || userData.businessUnit;
          setSelectedBusinessUnit(businessUnitId);
          const businessUnitData = businessUnits.find(bu => bu._id === businessUnitId);
          if (businessUnitData && businessUnitData.departments) {
            setDepartments(businessUnitData.departments);
          }
        }
        
        // Set form values
        form.setFieldsValue({
          name: userData.name || '',
          surname: userData.surname || '',
          email: userData.email || '',
          phoneNumber: phoneNumber,
          role: userData.group?.groupName || '',
          userType: userData.userType || 'regular',
          businessUnit: userData.businessUnit?._id || userData.businessUnit || undefined,
          department: userData.department || undefined,
          status: userData.status || 'active'
        });
      } else {
        message.error('User not found');
        navigate('/dashboard/team');
      }
    } catch (error) {
      console.error('Error loading user:', error);
      message.error('Failed to load user data');
      navigate('/dashboard/team');
    } finally {
      setPageLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    if (hasFormErrors) {
      message.error('Please fix all form errors before proceeding');
      return;
    }

    try {
      setLoading(true);
      
      // Prepare phone number object if provided
      const phoneNumberData = values.phoneNumber ? {
        countryCode: selectedCountryCode,
        number: values.phoneNumber,
        full: `${selectedCountryCode}${values.phoneNumber}`
      } : null;
      
      const updateData = {
        name: values.name,
        surname: values.surname,
        email: values.email,
        phoneNumber: phoneNumberData,
        groupId: Array.isArray(groups) ? groups.find(g => g.groupName === values.role)?._id : undefined,
        userType: values.userType,
        businessUnit: values.businessUnit || undefined,
        department: values.department || undefined,
        status: values.status
      };

      const result = await userService.updateUser(id, updateData);

      if (result.success) {
        message.success('Team member updated successfully!');
        navigate('/dashboard/team');
      } else {
 
       // alert('Update failed. Please contact your administrator.');
        // Show generic error message for any update failure
        if (result?.data.message && (result.message.includes('email') || result.message.includes('phone') || result.message.includes('duplicate') || result.message.includes('exists'))) {
          message.error(result?.data.message);
        } else {
          message.error('Update failed. Please contact your administrator.');
        }
      }
    } catch (error) {
      console.error('Update error:', error);
      message.error('Update failed. Please contact your administrator.');
    } finally {
      setLoading(false);
    }
  };

  if (pageLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '50vh' 
      }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', background: '#f5f5f5', minHeight: '100vh' }}>
      <div style={{ marginBottom: '24px' }}>
        <BreadcrumbNav breadcrumbs={BREADCRUMB_PATTERNS.teamEdit} />
      </div>

      <Row justify="center">
        <Col xs={24} sm={20} md={16} lg={12} xl={10}>
          <Card>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div >
                <Title level={2} style={{ margin: 0, color: '#2E86AB' }}>
                  {user?.name} {user?.surname}
                </Title>
                <Text type="secondary">Update member details</Text>
              </div>

              <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                disabled={loading}
              >
                <Form.Item
                  name="name"
                  label="First Name"
                  rules={[
                    { required: true, message: 'Please enter first name' },
                    { min: 2, message: 'Name must be at least 2 characters' }
                  ]}
                >
                  <Input 
                    prefix={<UserOutlined />} 
                    placeholder="Enter first name"
                    size="large"
                  />
                </Form.Item>

                <Form.Item
                  name="surname"
                  label="Last Name"
                  rules={[
                    { required: true, message: 'Please enter last name' },
                    { min: 2, message: 'Surname must be at least 2 characters' }
                  ]}
                >
                  <Input 
                    prefix={<UserOutlined />} 
                    placeholder="Enter last name"
                    size="large"
                  />
                </Form.Item>

                <Form.Item
                  name="email"
                  label="Email Address"
                  rules={[
                    { required: true, message: 'Please enter email address' },
                    { type: 'email', message: 'Please enter a valid email address' }
                  ]}
                >
                  <Input 
                    prefix={<MailOutlined />} 
                    placeholder="Enter email address"
                    size="large"
                  />
                </Form.Item>

                <PhoneNumberInput
                  form={form}
                  currentPhoneNumber={user?.phoneNumber?.full}
                  initialCountryCode={selectedCountryCode}
                  onCountryCodeChange={setSelectedCountryCode}
                  onValidationChange={setPhoneError}
                  disabled={loading}
                />

                <Form.Item
                  name="userType"
                  label="User Type"
                  rules={[{ required: true, message: 'Please select user type' }]}
                >
                  <Select 
                    placeholder="Select user type" 
                    size="large"
                    onChange={handleUserTypeChange}
                  >
                    <Option value="regular">Regular (Admin/Staff)</Option>
                    <Option value="participant">Participant (Learner)</Option>
                    <Option value="manager">Manager</Option>
                   
                  </Select>
                </Form.Item>

                {/* Group is required for regular users and optional for managers */}
                {(selectedUserType === 'regular' || selectedUserType === 'manager') && (
                  <Form.Item
                    name="role"
                    label="Group/Role"
                    rules={[{ 
                      required: selectedUserType === 'regular', 
                      message: 'Please select group' 
                    }]}
                    help={selectedUserType === 'regular' ? 'Required: Determines user permissions' : 'Optional for managers'}
                  >
                    <Select 
                      placeholder="Select group" 
                      size="large"
                      allowClear={selectedUserType === 'manager'}
                    >
                      {Array.isArray(groups) && groups.map(group => (
                        <Option key={group._id} value={group.groupName}>
                          {group.groupName}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                )}

                {/* Business Unit & Department - required for participants and managers, optional for regular */}
                <Form.Item
                  name="businessUnit"
                  label="Business Unit"
                  rules={[{ 
                    required: selectedUserType === 'participant' || selectedUserType === 'manager', 
                    message: 'Please select business unit' 
                  }]}
                  help={selectedUserType === 'regular' ? 'Optional for admin users' : undefined}
                >
                  <Select 
                    placeholder="Select business unit" 
                    size="large"
                    onChange={handleBusinessUnitChange}
                    allowClear={selectedUserType === 'regular'}
                  >
                    {Array.isArray(businessUnits) && businessUnits.map(bu => (
                      <Option key={bu._id} value={bu._id}>
                        {bu.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  name="department"
                  label="Department"
                  rules={[{ 
                    required: selectedUserType === 'participant' || selectedUserType === 'manager', 
                    message: 'Please select department' 
                  }]}
                  help={selectedUserType === 'regular' ? 'Optional for admin users' : undefined}
                >
                  <Select 
                    placeholder="Select department" 
                    size="large"
                    disabled={!selectedBusinessUnit || departments.length === 0}
                    allowClear={selectedUserType === 'regular'}
                  >
                    {Array.isArray(departments) && departments.map(dept => (
                      <Option key={dept} value={dept}>
                        {dept}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  name="status"
                  label="Status"
                  rules={[{ required: true, message: 'Please select status' }]}
                >
                  <Select placeholder="Select status" size="large">
                    <Option value="active">Active</Option>
                    <Option value="inactive">Inactive</Option>
                  </Select>
                </Form.Item>

                <Form.Item style={{ marginTop: '30px', marginBottom: 0 }}>
                  <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Button 
                      icon={<ArrowLeftOutlined />}
                      onClick={() => navigate('/dashboard/team')}
                      size="large"
                    >
                      Back to Team
                    </Button>
                    
                    <Button
                      
                      htmlType="submit"
                      icon={<SaveOutlined />}
                      loading={loading}
                      disabled={loading || hasFormErrors}
                      size="large"
                      style={{ 
                        background: '#2E86AB',
                        borderColor: '#2E86AB'
                      }}
                    >
                      Update Member
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default EditTeamMember;